import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(5),
  },
  empty: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  listItem: {
    alignItems: "flex-start",
  },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  protocol: {
    fontWeight: 600,
  },
  details: {
    display: "block",
    marginTop: theme.spacing(0.5),
  },
  lastMessage: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.primary,
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.75),
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

const statusColors = {
  open: "#2e7d32",
  pending: "#ed6c02",
  closed: "#616161",
};

const TicketHistoryModal = ({ open, handleClose, ticketId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async (page, append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/history`, {
        params: { pageNumber: page },
      });

      setTickets((previous) =>
        append ? [...previous, ...data.tickets] : data.tickets
      );
      setCount(data.count);
      setHasMore(data.hasMore);
      setPageNumber(page);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !ticketId) return;

    setTickets([]);
    setCount(0);
    setHasMore(false);
    setPageNumber(1);
    fetchHistory(1);
    // A abertura do modal e a troca de ticket reiniciam a paginação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  const handleOpenTicket = (historyTicketId) => {
    handleClose();
    history.push(`/tickets/${historyTicketId}`);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const statusLabel = (status) =>
    i18n.t(`ticketHistory.status.${status}`, { defaultValue: status });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {i18n.t("ticketHistory.title")}
        <Typography variant="body2" color="textSecondary">
          {i18n.t("ticketHistory.total", { count })}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && tickets.length === 0 ? (
          <div className={classes.loading}>
            <CircularProgress size={32} />
          </div>
        ) : tickets.length === 0 ? (
          <Typography className={classes.empty}>
            {i18n.t("ticketHistory.empty")}
          </Typography>
        ) : (
          <List disablePadding>
            {tickets.map((historyTicket, index) => (
              <React.Fragment key={historyTicket.id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  className={classes.listItem}
                  onClick={() => handleOpenTicket(historyTicket.id)}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <div className={classes.itemHeader}>
                        <Typography className={classes.protocol}>
                          {i18n.t("ticketHistory.protocol", {
                            id: historyTicket.id,
                          })}
                        </Typography>
                        <Chip
                          size="small"
                          label={statusLabel(historyTicket.status)}
                          style={{
                            backgroundColor:
                              statusColors[historyTicket.status] || "#616161",
                            color: "#fff",
                          }}
                        />
                      </div>
                    }
                    secondary={
                      <div>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.details}
                        >
                          {i18n.t("ticketHistory.openedAt")}: {formatDate(historyTicket.createdAt)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.details}
                        >
                          {i18n.t("ticketHistory.queue")}: {historyTicket.queue?.name || "-"}
                          {" • "}
                          {i18n.t("ticketHistory.agent")}: {historyTicket.user?.name || "-"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.details}
                        >
                          {i18n.t("ticketHistory.connection")}: {historyTicket.whatsapp?.name || "-"}
                        </Typography>
                        {historyTicket.lastMessage && (
                          <Typography
                            variant="body2"
                            className={classes.lastMessage}
                          >
                            {historyTicket.lastMessage}
                          </Typography>
                        )}
                        {historyTicket.tags?.length > 0 && (
                          <div className={classes.tags}>
                            {historyTicket.tags.map((tag) => (
                              <Chip
                                key={tag.id}
                                size="small"
                                label={tag.name}
                                style={{
                                  backgroundColor: tag.color || "#e0e0e0",
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {hasMore && (
          <div className={classes.loadMore}>
            <Button
              color="primary"
              disabled={loading}
              onClick={() => fetchHistory(pageNumber + 1, true)}
            >
              {loading
                ? i18n.t("ticketHistory.loading")
                : i18n.t("ticketHistory.loadMore")}
            </Button>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {i18n.t("ticketHistory.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketHistoryModal;
